/**
 * ReviewScheduleStep Component
 * Step 4: Contract summary, schedule generation, and submission
 */

import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Collapse,
  Grid,
  IconButton,
  Typography,
  Chip,
  Divider,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
} from '@mui/material';
import {
  Description as DescriptionIcon,
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowUp as CollapseIcon,
} from '@mui/icons-material';
import { useFormContext } from 'react-hook-form';
import { useContractFormContext } from '../ContractFormContext';
import { formatCurrency as formatCurrencyBase, formatRatePct } from '../../../../utils/formatters';
import AmortizationScheduleTable from '../../AmortizationScheduleTable';
import ScheduleEntryOverrideDialog from '../../ScheduleEntryOverrideDialog';
import ScheduleOverridesModal from '../../ScheduleOverridesModal';
import ContractInvestorsSection from '../../ContractInvestorsSection';
import { apiGet } from '../../../../utils/apiClient';
import logger from '../../../../utils/logger';
import { useTenant } from '../../../../contexts/TenantContext';
import { usePermissions } from '../../../../contexts/PermissionContext';
import { MODULES, PERMISSION_LEVELS } from '../../../../contexts/PermissionConstants';

function ReviewScheduleStep({ onGenerateSchedule }) {
  const { watch } = useFormContext();
  const { tenant, getStakeholderLabel } = useTenant();
  const dp = tenant?.roundingPrecision ?? 3;
  const issuerLabel = getStakeholderLabel('stakeholder.issuer.label', 'Issuer');
  const formatCurrency = (value, currencyCode) => formatCurrencyBase(value, currencyCode, dp);
  const {
    isEdit,
    isEditable,
    contractId,
    refData,
    hasSchedule,
    schedulePreview,
    setSchedulePreview,
    generatingSchedule,
    contractInvestors,
    adhocCapitalEvents,
    contractStampDuties,
    validationErrors,
    validationWarnings,
    initialData,
  } = useContractFormContext();
  const { hasPermission } = usePermissions();
  const canAdmin = hasPermission(MODULES.CONTRACT_MANAGEMENT, PERMISSION_LEVELS.ADMIN);
  const [contractSummaryExpanded, setContractSummaryExpanded] = useState(!hasSchedule);
  const [overrideDialogOpen, setOverrideDialogOpen] = useState(false);
  const [selectedOverrideEntry, setSelectedOverrideEntry] = useState(null);
  const [overridesModalOpen, setOverridesModalOpen] = useState(false);
  const [overridesModalEntry, setOverridesModalEntry] = useState(null);

  // Load schedule data on mount if in edit mode and schedule exists
  useEffect(() => {
    if (isEdit && contractId && hasSchedule && !schedulePreview) {
      loadScheduleData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- schedulePreview is a refetch guard; loadScheduleData is defined below
  }, [isEdit, contractId, hasSchedule]);

  const loadScheduleData = async () => {
    try {
      const response = await apiGet(`/amortization/contract/${contractId}`);
      const scheduleData = response?.data || response;
      if (scheduleData?.schedule?.length > 0) {
        setSchedulePreview(scheduleData);
      }
    } catch (err) {
      logger.error('Error loading schedule', { message: err.message });
    }
  };

  // Get form values
  const formValues = watch();

  // Resolve reference data lookups
  const investor = refData.investors?.find(i => i.id === formValues.investor_id);
  const product = refData.products?.find(p => p.id === formValues.product_id);
  const currency = refData.currencies?.find(c => c.id === formValues.investment_currency_id);
  const calculationProfile = refData.calculationProfiles?.find(p => p.id === parseInt(formValues.calculation_profile_id));
  const interestFreq = refData.frequencies?.find(f => f.id === formValues.interest_paid_frequency_id);
  const capitalFreq = refData.frequencies?.find(f => f.id === formValues.capital_paid_frequency_id);
  const broker = refData.brokers?.find(b => b.id === formValues.broker_id);
  const issuer = refData.issuers?.find(i => i.id === formValues.issuer_id);
  const collateralProvider = refData.collateralProviders?.find(p => p.id === formValues.collateral_provider_id);
  const collateralType = refData.collateralTypes?.find(t => t.id === formValues.collateral_type_id);
  const benchmarkRate = formValues.benchmark_rate_id
    ? refData.interestRates?.find(r => r.id === parseInt(formValues.benchmark_rate_id))
    : null;
  const capitalizationFreq = refData.frequencies?.find(f => f.id === formValues.capitalization_frequency_id);
  const repricingFreq = formValues.repricing_frequency_id
    ? refData.frequencies?.find(f => f.id === parseInt(formValues.repricing_frequency_id))
    : null;

  // Next re-pricing date: earliest first_repricing_date (or deposit_date) + N * months_value
  // strictly after today and before maturity. Mirrors fn_contract_get_by_id.
  const nextRepricingDate = (() => {
    if (!formValues.eligible_for_repricing) return null;
    if (!repricingFreq?.months_value) return null;
    if (!formValues.deposit_date || !formValues.maturity_date) return null;
    const anchorStr = formValues.first_repricing_date || formValues.deposit_date;
    const anchor = new Date(anchorStr);
    const maturity = new Date(formValues.maturity_date);
    if (Number.isNaN(anchor.getTime()) || Number.isNaN(maturity.getTime())) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    for (let step = 0; step < 10000; step += 1) {
      const candidate = new Date(anchor);
      candidate.setMonth(candidate.getMonth() + step * repricingFreq.months_value);
      if (candidate >= maturity) return null;
      if (candidate > today) {
        return candidate.toISOString().slice(0, 10);
      }
    }
    return null;
  })();

  const currencyCode = currency?.currency_code || 'ZAR';

  // Summary row component
  const SummaryRow = ({ label, value, highlight = false }) => (
    <TableRow>
      <TableCell
        component="th"
        scope="row"
        sx={{ fontWeight: 'medium', width: '40%', borderBottom: '1px solid', borderColor: 'divider' }}
      >
        {label}
      </TableCell>
      <TableCell sx={{ fontWeight: highlight ? 'bold' : 'normal', borderBottom: '1px solid', borderColor: 'divider' }}>
        {value || '-'}
      </TableCell>
    </TableRow>
  );

  return (
    <Box component="fieldset" disabled={!isEditable} sx={{ border: 'none', p: 0, m: 0 }}>
      {/* Warnings (errors are now shown by parent ContractWizard) */}
      {validationWarnings.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>Warnings:</Typography>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {validationWarnings.map((warning, index) => (
              <li key={index}>{warning}</li>
            ))}
          </ul>
        </Alert>
      )}
      {/* Contract Summary */}
      <Card sx={{ mb: 1 }}>
        <CardContent>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              mb: contractSummaryExpanded ? 2 : 0,
              cursor: 'pointer',
              px: 2,
              py: 1,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              backgroundColor: 'background.paper',
              '&:hover': { backgroundColor: 'action.hover' },
            }}
            onClick={() => setContractSummaryExpanded(prev => !prev)}
          >
            <DescriptionIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6" color="primary" sx={{ flexGrow: 1, fontSize: '1rem', fontWeight: 600 }}>
              Contract Summary
            </Typography>
            <IconButton size="small" aria-label={contractSummaryExpanded ? 'Collapse contract summary' : 'Expand contract summary'}>
              {contractSummaryExpanded ? <CollapseIcon /> : <ExpandIcon />}
            </IconButton>
          </Box>
          <Collapse in={contractSummaryExpanded} timeout="auto" unmountOnExit>
          <Grid container spacing={3}>
            {/* Basic Information */}
            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Basic Information
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableBody>
                    <SummaryRow
                      label="Investor"
                      value={
                        formValues.is_multi_investor
                          ? `Multiple Investors (${contractInvestors.length})`
                          : investor?.name
                      }
                    />
                    <SummaryRow label="Product Type" value={product?.type} />
                    <SummaryRow
                      label="Capital Amount"
                      value={formatCurrency(formValues.capital_amount, currencyCode)}
                      highlight
                    />
                    {parseFloat(formValues.rolled_funds_amount || 0) > 0 && (
                      <>
                        <SummaryRow label="New Funds" value={formatCurrency(formValues.new_funds_amount, currencyCode)} />
                        <SummaryRow label="Rolled Funds" value={formatCurrency(formValues.rolled_funds_amount, currencyCode)} />
                      </>
                    )}
                    {formValues.user_reference && (
                      <SummaryRow label="External Reference" value={formValues.user_reference} />
                    )}
                    <SummaryRow label="Currency" value={currency?.currency_code} />
                    <SummaryRow label="Likelihood to Roll" value={formValues.is_multi_investor ? 'Set per investor' : (formValues.likelihood_to_roll === 'Middle' ? 'Possible' : (formValues.likelihood_to_roll || 'Not specified'))} />
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>

            {/* Term & Dates */}
            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Investment Term & Dates
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableBody>
                    <SummaryRow
                      label="Investment Term"
                      value={`${formValues.investment_term_value} ${formValues.investment_term}`}
                    />
                    <SummaryRow label="Start Date" value={formValues.deposit_date} />
                    <SummaryRow label="Maturity Date" value={formValues.maturity_date} highlight />
                    <SummaryRow
                      label="Funds Received"
                      value={formValues.funds_received_date === formValues.deposit_date
                        ? `${formValues.funds_received_date} (same as start date)`
                        : formValues.funds_received_date || 'Same as start date'
                      }
                    />
                    <SummaryRow
                      label="First Payment Date"
                      value={formValues.first_payment_date || 'Same as start date'}
                    />
                    <SummaryRow
                      label="Eligible for Re-pricing"
                      value={formValues.eligible_for_repricing
                        ? `Yes (${repricingFreq?.frequency || '—'})`
                        : 'No'
                      }
                    />
                    {formValues.eligible_for_repricing && (() => {
                      const first = formValues.first_repricing_date;
                      const status = initialData?.status;
                      const isDraft = !status || status === 'Draft' || status === 'Pending Approval';
                      const label = isDraft ? 'First Re-pricing Date' : 'Next Re-pricing Date';
                      const value = isDraft ? (first || '—') : (nextRepricingDate || first || '—');
                      return <SummaryRow label={label} value={value} />;
                    })()}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>

            {/* Investors Section - for multi-investor contracts */}
            {formValues.is_multi_investor && contractInvestors.length > 0 && (
              <Grid size={12}>
                <ContractInvestorsSection
                  contractId={contractId}
                  investors={contractInvestors}
                  disabled={true}
                  currencyCode={currencyCode}
                />
              </Grid>
            )}

            {/* Interest & Payments */}
            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Interest & Payments
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableBody>
                    <SummaryRow label="Rate Type" value={formValues.rate_type} />
                    <SummaryRow
                      label="Interest Rate"
                      value={`${formValues.interest_rate}%`}
                      highlight
                    />
                    {formValues.rate_type === 'Variable' && (
                      <>
                        {benchmarkRate && (
                          <SummaryRow
                            label="Benchmark Rate"
                            value={`${benchmarkRate.interest_rate_type || benchmarkRate.rate_type}${
                              benchmarkRate.effective_interest_rate != null
                                ? ` (${parseFloat(benchmarkRate.effective_interest_rate).toFixed(2)}%)`
                                : ''
                            }`}
                          />
                        )}
                        {formValues.rate_spread != null && parseFloat(formValues.rate_spread) !== 0 && (
                          <SummaryRow
                            label="Rate Spread"
                            value={`${parseFloat(formValues.rate_spread) >= 0 ? '+' : ''}${parseFloat(formValues.rate_spread).toFixed(2)}%`}
                          />
                        )}
                        <SummaryRow
                          label="Rate Change Effective"
                          value={formValues.rate_change_effective_basis === 'anniversary' ? 'On Annual Anniversary' : 'On Date of Change'}
                        />
                      </>
                    )}
                    <SummaryRow
                      label="Day Count Basis"
                      value={formValues.day_count_basis ? `${formValues.day_count_basis} days` : '-'}
                    />
                    {formValues.exclude_leap_day && (
                      <SummaryRow label="Exclude Leap Day" value="Yes" />
                    )}
                    <SummaryRow label="Calculation Profile" value={calculationProfile?.name} />
                    <SummaryRow label="Interest Frequency" value={interestFreq?.frequency} />
                    <SummaryRow
                      label="Capital Frequency"
                      value={capitalFreq?.frequency || 'At Maturity'}
                    />
                    {capitalizationFreq && (
                      <SummaryRow label="Capitalization Frequency" value={capitalizationFreq.frequency} />
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>

            {/* Broker & Collateral */}
            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Broker & Collateral
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableBody>
                    <SummaryRow
                      label="Brokered"
                      value={
                        formValues.is_brokered ? (
                          <Chip label="Yes" size="small" color="primary" />
                        ) : (
                          'No'
                        )
                      }
                    />
                    {formValues.is_brokered && (
                      <>
                        <SummaryRow label="Broker" value={broker?.name} />
                        <SummaryRow
                          label="Broker Fees"
                          value={(() => {
                            const pct = parseFloat(formValues.broker_fees);
                            if (!isFinite(pct)) return '—';
                            const amount = (parseFloat(formValues.capital_amount) || 0) * pct / 100;
                            return `${formatRatePct(pct)}% (${formatCurrency(amount, currencyCode)})`;
                          })()}
                        />
                      </>
                    )}
                    {/* T113/T114: stamp duty is standalone and multi-row, shown
                        with its computed amount (capital × %), independent of broker. */}
                    {(contractStampDuties || []).map((sd, i) => {
                      const pct = parseFloat(sd.percentage);
                      const amount = isFinite(pct) ? (parseFloat(formValues.capital_amount) || 0) * pct / 100 : null;
                      return (
                        <SummaryRow
                          key={sd.id ?? i}
                          label={sd.reference || 'Stamp Duty'}
                          value={amount != null
                            ? `${formatRatePct(pct)}% (${formatCurrency(amount, currencyCode)})`
                            : `${formatRatePct(pct)}%`}
                        />
                      );
                    })}
                    {formValues.issuer_id && (
                      <SummaryRow label={issuerLabel} value={issuer?.name || '—'} />
                    )}
                    <SummaryRow
                      label="Has Collateral"
                      value={
                        formValues.has_collateral ? (
                          <Chip label="Yes" size="small" color="secondary" />
                        ) : (
                          'No'
                        )
                      }
                    />
                    {formValues.has_collateral && (
                      <>
                        <SummaryRow label="Provider" value={collateralProvider?.name} />
                        <SummaryRow label="Type" value={collateralType?.type} />
                        <SummaryRow
                          label="Amount"
                          value={formatCurrency(formValues.collateral_amount, currencyCode)}
                        />
                      </>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>

            {/* Scheduled Capital Movements (withdrawals + injections) */}
            {(() => {
              const capitalMovements = adhocCapitalEvents.filter(
                e => e.event_type === 'withdrawal' || e.event_type === 'injection'
              );
              if (capitalMovements.length === 0) return null;
              return (
                <Grid size={12}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Scheduled Capital Movements ({capitalMovements.length})
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableBody>
                        {capitalMovements.map((event, index) => {
                          const amt = parseFloat(event.amount || 0);
                          const sign = event.event_type === 'injection' ? '+' : '-';
                          return (
                            <SummaryRow
                              key={event.id || index}
                              label={`${event.event_date || 'No date'}`}
                              value={`${sign} ${formatCurrency(Math.abs(amt), currencyCode)} ${event.description ? `(${event.description})` : ''}`}
                            />
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
              );
            })()}

            {/* Adhoc Interest Accruals (carried from prior contract) */}
            {(() => {
              const accruals = adhocCapitalEvents.filter(e => e.event_type === 'interest_accrual');
              if (accruals.length === 0) return null;
              const netAccrual = accruals.reduce((sum, e) => sum + parseFloat(e.amount || 0), 0);
              return (
                <Grid size={12}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Adhoc Interest Accruals ({accruals.length})
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableBody>
                        {accruals.map((event, index) => {
                          const amt = parseFloat(event.amount || 0);
                          const sign = amt >= 0 ? '+' : '-';
                          return (
                            <SummaryRow
                              key={event.id || index}
                              label={`${event.event_date || 'No date'}`}
                              value={`${sign} ${formatCurrency(Math.abs(amt), currencyCode)} ${event.description ? `(${event.description})` : ''}`}
                            />
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <Alert severity="info" sx={{ mt: 1 }}>
                    {accruals.length} Interest Accrual entr{accruals.length === 1 ? 'y' : 'ies'} totalling {netAccrual >= 0 ? '+' : '-'}{formatCurrency(Math.abs(netAccrual), currencyCode)} will be paid through the first Adhoc Interest Payment on/after each accrual date (or rolled into the final maturity payment if none).
                  </Alert>
                </Grid>
              );
            })()}
          </Grid>
          </Collapse>
        </CardContent>
      </Card>
      {/* Schedule Generation Section */}
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Amortization Schedule
          </Typography>

          {(
            // Show schedule section
            (<Box>
              {generatingSchedule ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
                  <CircularProgress />
                  <Typography sx={{ ml: 2 }}>Generating schedule...</Typography>
                </Box>
              ) : hasSchedule && schedulePreview?.schedule?.length > 0 ? (
                <>
                  <AmortizationScheduleTable
                    schedule={schedulePreview.schedule || []}
                    showAllRows
                    contractId={contractId}
                    contract={{
                      ...(schedulePreview.contract || formValues),
                      id: contractId,
                      status: initialData?.status,
                      calculation_profile_name: calculationProfile?.name,
                      calculation_profile_interest_method: calculationProfile?.interest_method,
                      calculation_profile_formula: calculationProfile?.formula_display,
                      calculation_profile_compounding_frequency: calculationProfile?.compounding_frequency,
                    }}
                    currencyCode={currencyCode}
                    canAdmin={canAdmin}
                    onRegenerate={onGenerateSchedule}
                    hideRegenerateButton
                    onOverrideRequest={(entry) => {
                      setSelectedOverrideEntry(entry);
                      setOverrideDialogOpen(true);
                    }}
                    onShowOverrides={(entry) => {
                      setOverridesModalEntry(entry);
                      setOverridesModalOpen(true);
                    }}
                  />
                  <ScheduleEntryOverrideDialog
                    open={overrideDialogOpen}
                    onClose={() => setOverrideDialogOpen(false)}
                    contract={{
                      id: contractId,
                      status: initialData?.status,
                      equal_installment: formValues.equal_installment,
                      combine_payment_rows: formValues.combine_payment_rows,
                      currency_code: currency?.currency_code,
                      ...(schedulePreview.contract || {}),
                    }}
                    scheduleEntry={selectedOverrideEntry}
                    onSubmitSuccess={() => {
                      setOverrideDialogOpen(false);
                      loadScheduleData();
                    }}
                  />
                  <ScheduleOverridesModal
                    open={overridesModalOpen}
                    onClose={() => {
                      setOverridesModalOpen(false);
                      setOverridesModalEntry(null);
                    }}
                    contract={{
                      id: contractId,
                      status: initialData?.status,
                      equal_installment: formValues.equal_installment,
                      combine_payment_rows: formValues.combine_payment_rows,
                      currency_code: currency?.currency_code,
                      ...(schedulePreview.contract || {}),
                    }}
                    scheduleEntry={overridesModalEntry}
                    onChanged={() => loadScheduleData()}
                  />
                </>
              ) : hasSchedule ? (
                <Typography color="text.secondary">
                  Loading schedule data...
                </Typography>
              ) : (
                <Alert severity="info">
                  Click "Generate Schedule" to create the amortization schedule. Your contract data will be saved automatically.
                </Alert>
              )}
            </Box>)
          )}
        </CardContent>
      </Card>
    </Box>
  );
}

export default ReviewScheduleStep;
